import { EventEmitter } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { controlsUpdatedOnBlur } from '../../shared/utilities/Constants';

/**
 *
 * @param source a Key/Value pair representing a form control and its value. Should line up with what's in NGRX
 * @param destinationGroup the FormGroup that will be modified
 * @param validationMapper a function that takes a control name as an argument and returns a ValidatorFn array that will be set on the control if it doesn't exist
 * @param afterBuildEmitter an event emitter which emits no value to call after the form group has been created
 * @returns No return value - this function modifies the destinationGroup by adding or updating controls with prefilled values
 */
export function buildFromPrefilledForm(
  source: { [key: string]: any } | undefined,
  destinationGroup: UntypedFormGroup,
  validationMapper?: (controlName: string) => ValidatorFn[],
  afterBuildEmitter: EventEmitter<any> | null = null
): void {
  if (!source) return;

  const names = Object.keys(source);

  names.forEach((control) => {
    if (destinationGroup?.controls[control]) {
      destinationGroup.controls[control].setValue(source[control]);
    } else {
      if (validationMapper) {
        destinationGroup.setControl(control, new UntypedFormControl(source[control], validationMapper(control)));
      }
      if (controlsUpdatedOnBlur.includes(control)) {
        destinationGroup.setControl(control, new UntypedFormControl(source[control], { updateOn: 'blur' }))
      }
      else {
        destinationGroup.setControl(control, new UntypedFormControl(source[control]));
      }
    }
  });

  if (afterBuildEmitter) afterBuildEmitter.emit();
}

/* manually reset value with '' as default until we upgrade to an angular version that supports FormControlOptions
    which contains a flag 'initialValueIsDefault' that will allow us to just use FormControl/FormGroup.reset()
*/
/**
 *
 * @param group The form group that will have its' controls looped over and reset with default values
 * @description Loops over controls in a FormGroup and calls reset() with a default value based on the value's type.
 *              - string = ''
 *              - boolean = false
 *              - number = 0
 */
export function resetGroup(group: UntypedFormGroup): void {
  Object.keys(group.controls).forEach(controlName => {
    const control = group.controls[controlName];
    if (isFormGroup(control)) {
      resetGroup(control);
    } else {
      const controlValue = group.controls[controlName].value;
      if (typeof controlValue === 'boolean') {
        group.controls[controlName].reset(false);
      } else if (typeof controlValue === 'string') {
        group.controls[controlName].reset('');
      } else if (typeof controlValue === 'number') {
        group.controls[controlName].reset(0);
      }
    }
  });
}

export function updateGroupValueAndValidity(group: UntypedFormGroup): void {
  Object.keys(group.controls).forEach(controlName => {
    const control = group.controls[controlName];
    if (isFormGroup(control)) {
      updateGroupValueAndValidity(control);
    } else {
      const control = group.controls[controlName];
      if (control.invalid) {
        control.updateValueAndValidity();
      }
    }
  });
}

/**
 *
 * @param formGroup The form group that will have its controls looped over and checked if its invalid
 *        - Important : Doesn't handle nested form groups
 * @returns Returns false when it finds a invalid control. Returns true if all controls are valid.
 */
export function checkIfAllControlsAreValid(formGroup: UntypedFormGroup): boolean {
  let control = Object.keys(formGroup.controls).find(element => formGroup.controls[element].invalid);
  if (control) { return false; }
  return true;
}

function isFormGroup(control: AbstractControl): control is UntypedFormGroup {
  return control instanceof UntypedFormGroup
}

/**
 *
 * @param parentForm parentFormGroup in context
 * @param name name of the formControl
 * @param value formControl value
 * @param validators array of ValidatorFn
 * @param asyncValidators array of AsyncValidatorFn
 * @param disabled true if the formControl should be disabled
 * @param setValues true if we want to change the value of existing control
 * @param updatedOn condition for when we want the formControl value to update
 * @param markAsDirty whether or not to mark the control as dirty, defaulted to true if not specified otherwise. 
 * @returns No return value - this function adds control and its validators to formGroup if not present 
 */
export function createControl(
  parentForm: UntypedFormGroup,
  name: string,
  value: string | boolean,
  validators?: ValidatorFn[],
  asyncValidators?: AsyncValidatorFn[],
  disabled?: boolean,
  setValues?: boolean,
  updatedOn?: 'change' | 'blur' | 'submit',
  markAsDirty: boolean = true
) {
  if (!parentForm.get(name)) {
    parentForm.setControl(
      name,
      new UntypedFormControl(value, {
        validators: validators || [],
        asyncValidators: asyncValidators || [],
        updateOn: updatedOn || 'change'
      })
    );
  } else {
    const control = parentForm.get(name);

    if (setValues) {
      control?.setValue(value);
    }
    if (!control?.validator && validators) {
      control?.setValidators(validators);
    }

    if (!control?.asyncValidator && asyncValidators) {
      control?.setAsyncValidators(asyncValidators);
    }
    if (markAsDirty && control?.value) {
      parentForm.get(name)?.markAsDirty();
    }
    control?.updateValueAndValidity();
  }
  if (disabled) {
    parentForm.get(name)?.disable();
  }
}